/**
 * SIM-MGMT — EngineeringSimCard
 *
 * One saved-simulation card in the site "Engineering Analysis" tab. This is the
 * richer, card-shaped sibling of SavedSimRow (the dense FILE ▸ Load Model list
 * row). It reuses the EXACT same additive store actions and confirm-gating
 * pattern as SavedSimRow:
 *
 *   - OPEN/LOAD -> onOpen(sim)                         (host loads into canvas)
 *   - RENAME    -> renameCloudSim(cloudId, name)       (inline dialog)
 *   - RESTORE   -> listSimBackups + restoreSimBackup    (confirm — replaces canvas)
 *   - DELETE    -> deleteCloudSim(cloudId)              (confirm — destructive)
 *
 * GUARDRAILS (mirrors SavedSimRow):
 *   - DELETE always confirms before calling the store, and only ever targets
 *     THIS card's cloudId — never a batch.
 *   - RESTORE replaces the live canvas: confirms before calling.
 *   - Store actions return { ok, error? } and never throw — we branch on ok.
 *   - onChanged() is called after a successful mutate so the host refreshes
 *     its list.
 *
 * Lives under src/components/sim (not src/components/siterag) so it is outside
 * the design-tokens chrome sentinel scan AND unit-testable in isolation.
 */
import React from 'react';
import { Trash2, Pencil, History, MoreVertical } from 'lucide-react';
import type { CloudSimActionResult, SimBackupRow } from '../../store';
import type { EoSolveResult } from '../../lib/sim/eo/eoReportSection';
import { SimModelPreview, summariseModel } from './SimModelPreview';
import { StatusPill, type StatusPillVariant } from '../ui/aurora';
import {
  deriveClosure,
  closureBarWidth,
  closureFoot,
  formatClosurePct,
} from './closureGauge';
import './engineeringBench.css';

/** Minimal shape of a saved-sim item this card needs. Matches SiteLandingPage
 *  `allSims` rows (cloud + in-memory union). */
export interface EngineeringSim {
  id: string;
  name: string;
  cloudId?: string;
  tabId?: string;
  version?: number;
  createdAt?: string;
  isInMemory?: boolean;
  /** Historic serialized marker: preserved source model, read-only until forked. */
  sourceBoP?: string;
  /** Generated/imported graph that must clear EO readiness before solving. */
  repairMode?: boolean;
  eoCapability?: string;
}

export interface EngineeringSimCardProps {
  sim: EngineeringSim;
  /** Whether this card maps to the currently-active editor tab (the model that
   *  RUN/SOLVE targets). Surfaces an "ACTIVE" marker. */
  isActiveModel?: boolean;
  /** Persisted JSON blobs for the structural preview (cloud sims only). */
  nodesJson?: string | null;
  edgesJson?: string | null;
  /**
   * Optional solved-run summary for THIS model. When present, the card's
   * instrument gauges (net power · plant η · closure) read live figures; when
   * absent, the gauges render "awaiting solve" — we never fabricate physics.
   */
  result?: EoSolveResult | null;
  /** Open/focus this sim in the canvas. Host owns tab focus vs. cloud-load. */
  onOpen: (sim: EngineeringSim) => void;
  /** Cloud management actions — only meaningful when sim.cloudId is set. */
  deleteCloudSim?: (cloudId: string) => Promise<CloudSimActionResult>;
  renameCloudSim?: (cloudId: string, name: string) => Promise<CloudSimActionResult>;
  listSimBackups?: (cloudId: string) => Promise<SimBackupRow[]>;
  restoreSimBackup?: (cloudId: string, version: number) => Promise<CloudSimActionResult>;
  /** Called after a successful mutate (delete/rename) so the list refreshes. */
  onChanged: () => void;
  /** Optional toast hooks. Defaults to no-ops for tests. */
  notify?: { success?: (msg: string) => void; error?: (msg: string) => void };
}

type DialogMode = null | 'rename' | 'delete' | 'restore';

/** Friendly short glyph labels for the thermo-bus rail (the mini
 *  component-chain that reads as "this is a thermodynamic cycle"). Derived from
 *  the model's node types — never fabricated. */
const BUS_GLYPH: Record<string, string> = {
  gasTurbine: 'GT',
  gasTurbineUnit: 'GT',
  combustor: 'COMB',
  hrsg: 'HRSG',
  boiler: 'BOILER',
  turbine: 'TURB',
  condenser: 'COND',
  airCooledCondenser: 'ACC',
  pump: 'PUMP',
  compressor: 'COMP',
  heatExchanger: 'HX',
  reactor: 'RX',
  dataCenter: 'DC',
  coolingTower: 'CT',
  generator: 'GEN',
  separator: 'SEP',
  reheater: 'RHT',
  saturator: 'SAT',
};

/** Up to 5 bus nodes from the model's equipment types (plumbing already
 *  collapsed out by summariseModel). Empty when the model has no nodes. */
function busNodesFromModel(nodesJson?: string | null, edgesJson?: string | null): string[] {
  const { byType } = summariseModel(nodesJson, edgesJson);
  return byType
    .slice(0, 5)
    .map(({ type }) => BUS_GLYPH[type] ?? type.replace(/([a-z])([A-Z])/g, '$1 $2').toUpperCase().slice(0, 6));
}

export function EngineeringSimCard({
  sim,
  isActiveModel = false,
  nodesJson,
  edgesJson,
  result,
  onOpen,
  deleteCloudSim,
  renameCloudSim,
  listSimBackups,
  restoreSimBackup,
  onChanged,
  notify,
}: EngineeringSimCardProps) {
  const name = sim.name || 'Untitled';
  const hasCloud = !!sim.cloudId;
  const isLegacySource = typeof sim.sourceBoP === 'string' && sim.sourceBoP.trim().length > 0;
  const needsRepair = isLegacySource
    || sim.repairMode === true
    || sim.eoCapability === 'needs-migration'
    || sim.eoCapability === 'unavailable';

  // ── Instrument readout ── derive net power · plant η · closure from the
  // optional solved-run summary. NO physics is fabricated: absent fields read
  // "—"/"awaiting solve" and the closure gauge degrades to "n/a".
  const hasResult = !!result;
  const solved = result?.converged === true;
  const cycle = solved
    ? result.conservation.fuel_cycle ?? result.conservation.cycle
    : undefined;
  const netPowerMw = cycle && Number.isFinite(cycle.W_net) ? cycle.W_net / 1000 : null;
  const eta = solved
    ? result.conservation.fuel_cycle?.eta_fuel_lhv
      ?? (result.conservation.cycle && result.conservation.cycle.Q_in > 0
      ? result.conservation.cycle.W_net / result.conservation.cycle.Q_in
      : null)
    : null;
  const etaPct = eta != null && Number.isFinite(eta) ? eta * 100 : null;
  const closure = deriveClosure(result);
  const closureFootInfo = closureFoot(closure);
  const busNodes = React.useMemo(() => busNodesFromModel(nodesJson, edgesJson), [nodesJson, edgesJson]);

  // Verdict spine + status pill follow the closure tier when solved; an unsolved
  // model is a neutral draft.
  const spineClass = !hasResult
    ? 'eng-inst__spine'
    : !solved
      ? 'eng-inst__spine eng-inst__spine--nogo'
    : closure.tier === 'go'
      ? 'eng-inst__spine eng-inst__spine--go'
      : closure.tier === 'cond'
        ? 'eng-inst__spine eng-inst__spine--cond'
        : closure.tier === 'nogo'
          ? 'eng-inst__spine eng-inst__spine--nogo'
          : 'eng-inst__spine';
  const flagged = hasResult && (!solved || closure.tier === 'nogo');
  const statusVariant: StatusPillVariant = needsRepair
    ? 'cond'
    : !hasResult
    ? isActiveModel
      ? 'info'
      : 'neutral'
    : !solved || closure.tier === 'nogo'
      ? 'nogo'
      : isActiveModel
        ? 'info'
        : 'go';
  // A canvas is an editable model, not a solved authority. Verified EO results
  // are surfaced separately as "Solved" and the site-wide authority picker is
  // the only place that can say "Used for analyses".
  const statusLabel = needsRepair
    ? isLegacySource ? 'Legacy · repair' : 'Repair required'
    : !hasResult
    ? 'Editing'
    : !solved
      ? 'Invalid result'
    : closure.tier === 'nogo'
      ? 'Closure drift'
      : isActiveModel
        ? 'Active'
        : 'Solved';

  const [menuOpen, setMenuOpen] = React.useState(false);
  const [dialog, setDialog] = React.useState<DialogMode>(null);
  const [renameValue, setRenameValue] = React.useState(name);
  const [busy, setBusy] = React.useState(false);
  const [backups, setBackups] = React.useState<SimBackupRow[]>([]);
  const [selectedVersion, setSelectedVersion] = React.useState<number | null>(null);
  const [backupsLoading, setBackupsLoading] = React.useState(false);

  const ok = (msg: string) => notify?.success?.(msg);
  const fail = (msg: string) => notify?.error?.(msg);

  const closeAll = () => {
    setDialog(null);
    setMenuOpen(false);
    setBusy(false);
  };

  const openRename = () => {
    setRenameValue(name);
    setDialog('rename');
    setMenuOpen(false);
  };

  const openDelete = () => {
    setDialog('delete');
    setMenuOpen(false);
  };

  const openRestore = async () => {
    if (!sim.cloudId || !listSimBackups) return;
    setDialog('restore');
    setMenuOpen(false);
    setBackupsLoading(true);
    setSelectedVersion(null);
    const rows = await listSimBackups(sim.cloudId);
    setBackups(rows);
    setSelectedVersion(rows.length > 0 ? rows[0].version : null);
    setBackupsLoading(false);
  };

  const confirmRename = async () => {
    if (!sim.cloudId || !renameCloudSim) return;
    const trimmed = renameValue.trim();
    if (!trimmed) {
      fail('Name cannot be empty');
      return;
    }
    setBusy(true);
    const res = await renameCloudSim(sim.cloudId, trimmed);
    if (res.ok) {
      ok(`Renamed to "${trimmed}"`);
      closeAll();
      onChanged();
    } else {
      setBusy(false);
      fail(res.error || 'Rename failed');
    }
  };

  const confirmDelete = async () => {
    if (!sim.cloudId || !deleteCloudSim) {
      fail('This simulation is not saved to the cloud yet');
      return;
    }
    setBusy(true);
    const res = await deleteCloudSim(sim.cloudId);
    if (res.ok) {
      ok(`Deleted "${name}"`);
      closeAll();
      onChanged();
    } else {
      setBusy(false);
      fail(res.error || 'Delete failed');
    }
  };

  const confirmRestore = async () => {
    if (!sim.cloudId || !restoreSimBackup) return;
    if (selectedVersion == null) {
      fail('Select a backup version');
      return;
    }
    setBusy(true);
    const res = await restoreSimBackup(sim.cloudId, selectedVersion);
    if (res.ok) {
      ok(`Restored "${name}" to v${selectedVersion}`);
      closeAll();
    } else {
      setBusy(false);
      fail(res.error || 'Restore failed');
    }
  };

  return (
    <article
      className={`eng-inst group${isActiveModel ? ' eng-inst--active' : ''}${flagged ? ' eng-inst--flagged' : ''}`}
      data-testid={`engineering-sim-card-${sim.id}`}
      aria-current={isActiveModel ? 'true' : undefined}
    >
      {/* ── left identity rail: verdict spine + name + meta + thermo bus ── */}
      <div className="eng-inst__id">
        <span className={spineClass} aria-hidden />
        <div className="eng-inst__idbody">
          <div className="eng-inst__namerow">
            <span className="eng-inst__name" title={name}>{name}</span>
            <StatusPill variant={statusVariant}>{statusLabel}</StatusPill>
            {isActiveModel && (
              <span
                className="sr-only"
                data-testid={`engineering-sim-active-${sim.id}`}
                title={needsRepair
                  ? 'This is the active editor model; solving remains locked until repair is complete'
                  : 'This is the active model targeted by the solver'}
              >
                Active
              </span>
            )}
          </div>

          <div className="eng-inst__meta">
            {sim.version != null && (
              <>
                <span><b>v{sim.version}</b></span>
                <span className="eng-inst__sep">·</span>
              </>
            )}
            {hasCloud ? (
              <span style={{ color: 'var(--success)' }}>cloud saved</span>
            ) : (
              <span>unsaved draft</span>
            )}
            {needsRepair && (
              <>
                <span className="eng-inst__sep">·</span>
                <span data-testid={`engineering-sim-repair-${sim.id}`} style={{ color: 'var(--warning)' }}>
                  {isLegacySource ? 'preserved source — create repair copy' : 'clear readiness blockers'}
                </span>
              </>
            )}
            {sim.createdAt && (
              <>
                <span className="eng-inst__sep">·</span>
                <span><b>{new Date(sim.createdAt).toLocaleDateString()}</b></span>
              </>
            )}
          </div>

          {/* Thermo bus rail — derived from the model's component types. */}
          {busNodes.length > 0 && (
            <div className="eng-bus" aria-hidden>
              {busNodes.map((node, i) => (
                <React.Fragment key={`${node}-${i}`}>
                  {i > 0 && <span className="eng-bus__link" />}
                  <span className="eng-bus__node">
                    {i === 0 && <span className="pip" />}
                    {node}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Structural preview retained (node/edge + equipment histogram). */}
          <SimModelPreview nodesJson={nodesJson} edgesJson={edgesJson} />
        </div>
      </div>

      {/* ── right column: gauge cluster + actions ── */}
      <div className="eng-inst__actions">
        <div className={`eng-gauges${solved ? '' : ' eng-gauges--idle'}`} data-testid={`engineering-sim-gauges-${sim.id}`}>
          <div className="eng-gauge">
            <span className="eng-gauge__k">Net power</span>
            <span className="eng-gauge__v eng-gauge__v--accent">
              {netPowerMw !== null ? netPowerMw.toFixed(1) : '—'}
              {netPowerMw !== null && <small>MW</small>}
            </span>
            <span className="eng-gauge__foot">{solved ? 'net electrical' : hasResult ? 'invalid run' : 'awaiting solve'}</span>
          </div>
          <div className="eng-gauge">
            <span className="eng-gauge__k">Plant&nbsp;η</span>
            <span className="eng-gauge__v">
              {etaPct !== null ? etaPct.toFixed(1) : '—'}
              {etaPct !== null && <small>%</small>}
            </span>
            <span className="eng-gauge__foot">{etaPct !== null && solved ? 'thermal' : hasResult ? 'invalid run' : 'awaiting solve'}</span>
          </div>
          <div className="eng-gauge">
            <span className="eng-gauge__k">Closure</span>
            <span className={`eng-gauge__v${closure.tier === 'nogo' ? ' eng-gauge__v--danger' : ''}`}>
              {closure.pct !== null ? formatClosurePct(closure.pct) : '—'}
              {closure.pct !== null && <small>%</small>}
            </span>
            <div className="eng-cbar">
              <div
                className={`eng-cbar__fill${closure.tier !== 'na' ? ` eng-cbar__fill--${closure.tier}` : ''}`}
                style={{ width: `${closureBarWidth(closure)}%` }}
              />
            </div>
            <span className={`eng-gauge__foot${closureFootInfo.cue !== 'na' ? ` eng-gauge__foot--${closureFootInfo.cue}` : ''}`}>
              {closureFootInfo.label}
            </span>
          </div>
        </div>

        {/* Actions: explicit OPEN/LOAD + kebab for rename/restore/delete */}
        <div className="eng-inst__act-row">
          <button
            type="button"
            onClick={() => onOpen(sim)}
            className={`da-btn${isActiveModel ? ' da-btn--primary' : ''}`}
            aria-label={`Open ${name}`}
            data-testid={`engineering-sim-open-${sim.id}`}
          >
            Open sheet →
          </button>

          {hasCloud && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setMenuOpen((o) => !o);
              }}
              className="eng-kebab"
              aria-label={`Actions for ${name}`}
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              data-testid={`engineering-sim-actions-${sim.id}`}
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {menuOpen && (
        <div
          role="menu"
          className="absolute right-2 top-10 z-50 w-40 rounded-md overflow-hidden border border-border bg-background/95 shadow-lg"
        >
          {renameCloudSim && (
            <button
              type="button"
              role="menuitem"
              onClick={openRename}
              className="flex w-full items-center px-3 py-1.5 type-meta hover:bg-muted text-text-primary"
            >
              <Pencil className="w-3 h-3 mr-2" /> Rename
            </button>
          )}
          {listSimBackups && restoreSimBackup && (
            <button
              type="button"
              role="menuitem"
              onClick={() => void openRestore()}
              className="flex w-full items-center px-3 py-1.5 type-meta hover:bg-muted text-text-primary"
            >
              <History className="w-3 h-3 mr-2" /> Restore backup
            </button>
          )}
          {deleteCloudSim && (
            <button
              type="button"
              role="menuitem"
              onClick={openDelete}
              className="flex w-full items-center px-3 py-1.5 type-meta hover:bg-danger/10 text-danger"
            >
              <Trash2 className="w-3 h-3 mr-2" /> Delete
            </button>
          )}
        </div>
      )}

      {/* RENAME dialog */}
      {dialog === 'rename' && (
        <div role="dialog" aria-label="Rename simulation" className="absolute right-2 top-10 z-50 w-64 rounded-md overflow-hidden border border-border bg-background/95 p-3 shadow-lg">
          <label className="block type-meta text-text-muted mb-1">Rename simulation</label>
          <input
            type="text"
            value={renameValue}
            autoFocus
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void confirmRename();
              if (e.key === 'Escape') closeAll();
            }}
            className="w-full rounded-md border border-border-strong bg-background px-2 py-1 type-body text-text-primary"
            aria-label="New name"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={closeAll} disabled={busy} className="rounded-md px-2 py-1 type-meta text-text-muted hover:text-text-primary">Cancel</button>
            <button type="button" onClick={() => void confirmRename()} disabled={busy} className="rounded-md px-2 py-1 type-meta bg-muted text-text-primary hover:bg-state-hover">Save</button>
          </div>
        </div>
      )}

      {/* DELETE confirm dialog */}
      {dialog === 'delete' && (
        <div role="dialog" aria-label="Delete simulation" className="absolute right-2 top-10 z-50 w-64 rounded-md overflow-hidden border border-danger/50 bg-background/95 p-3 shadow-lg">
          <p className="type-body text-text-primary leading-snug">
            Delete "{name}"? This cannot be undone.
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={closeAll} disabled={busy} className="rounded-md px-2 py-1 type-meta text-text-muted hover:text-text-primary">Cancel</button>
            <button
              type="button"
              onClick={() => void confirmDelete()}
              disabled={busy}
              data-testid={`engineering-sim-confirm-delete-${sim.id}`}
              className="rounded-md px-2 py-1 type-meta bg-danger/20 text-danger hover:bg-danger/30"
            >
              Delete
            </button>
          </div>
        </div>
      )}

      {/* RESTORE backup dialog */}
      {dialog === 'restore' && (
        <div role="dialog" aria-label="Restore backup" className="absolute right-2 top-10 z-50 w-72 rounded-md overflow-hidden border border-border bg-background/95 p-3 shadow-lg">
          <label className="block type-meta text-text-muted mb-1">Restore backup</label>
          {backupsLoading ? (
            <div className="py-2 type-meta text-text-muted italic">Loading backups…</div>
          ) : backups.length === 0 ? (
            <div className="py-2 type-meta text-text-muted italic">No backups available</div>
          ) : (
            <select
              value={selectedVersion ?? ''}
              onChange={(e) => setSelectedVersion(Number(e.target.value))}
              aria-label="Backup version"
              className="w-full rounded-md border border-border-strong bg-background px-2 py-1 type-body text-text-primary"
            >
              {backups.map((b) => (
                <option key={b.version} value={b.version}>
                  v{b.version}
                  {b.savedAt ? ` · ${b.savedAt.slice(0, 19).replace('T', ' ')}` : ''}
                </option>
              ))}
            </select>
          )}
          <p className="mt-2 type-meta text-text-muted leading-snug">
            Restoring opens this snapshot in the live canvas.
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <button type="button" onClick={closeAll} disabled={busy} className="rounded-md px-2 py-1 type-meta text-text-muted hover:text-text-primary">Cancel</button>
            <button
              type="button"
              onClick={() => void confirmRestore()}
              disabled={busy || backups.length === 0 || selectedVersion == null}
              data-testid={`engineering-sim-confirm-restore-${sim.id}`}
              className="rounded-md px-2 py-1 type-meta bg-muted text-text-primary hover:bg-state-hover disabled:opacity-40"
            >
              Restore
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
